import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, Users, Building2, ShoppingBag, MessageSquare, TrendingUp, 
  DollarSign, Eye, LogOut, RefreshCw, Loader2, CheckCircle, XCircle,
  Clock, BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Package,
  Phone, FileText, Ship, MapPin, Mail, AlertTriangle, Ban, History, Crown,
  BookOpen, Plus, Pencil, Trash2, Activity, Globe, ChevronDown, Rocket, Minus,
  Video, Play
} from 'lucide-react';
import { toast } from 'sonner';
import { FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge, PartialVerificationBadge } from '@/components/VerificationBadges';
import AdminVideoVerificationQueue from '@/components/AdminVideoVerificationQueue';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState({ sellers: [], buyers: [] });
  const [inquiries, setInquiries] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [admin, setAdmin] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  
  // Seller detail dialog
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showSellerDialog, setShowSellerDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [phoneVisibilitySettings, setPhoneVisibilitySettings] = useState({});

  // Active section for dropdown navigation
  const [activeSection, setActiveSection] = useState('companies');

  // Blog management state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    category: 'export-tips',
    excerpt: '',
    content: '',
    cover_image: '',
    tags: '',
    published: true
  });

  // Founding Program state
  const [foundingCount, setFoundingCount] = useState({ claimed: 0, total_slots: 500, total_exporters: 0 });
  const [adjustingCount, setAdjustingCount] = useState(false);

  // Buyer Verifications state
  const [buyerVerifications, setBuyerVerifications] = useState([]);
  const [buyerVerificationsLoading, setBuyerVerificationsLoading] = useState(false);
  const [selectedBuyerVerification, setSelectedBuyerVerification] = useState(null);
  const [buyerVerificationFilter, setBuyerVerificationFilter] = useState('pending');

  // Seller Verifications state
  const [sellerVerifications, setSellerVerifications] = useState([]);
  const [sellerVerificationsLoading, setSellerVerificationsLoading] = useState(false);
  const [selectedSellerVerification, setSelectedSellerVerification] = useState(null);
  const [sellerVerificationFilter, setSellerVerificationFilter] = useState('pending');

  // Video Verifications state
  const [videoVerifications, setVideoVerifications] = useState([]);
  const [videoVerificationsLoading, setVideoVerificationsLoading] = useState(false);
  const [selectedVideoVerification, setSelectedVideoVerification] = useState(null);
  const [videoStats, setVideoStats] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  // Section options for dropdown
  const sectionOptions = [
    { value: 'companies', label: 'Companies', icon: Building2 },
    { value: 'inquiries', label: 'Inquiries', icon: MessageSquare },
    { value: 'pipeline', label: 'Deal Pipeline', icon: TrendingUp },
    { value: 'analytics', label: 'Analytics', icon: Activity },
    { value: 'sellers', label: 'Seller Verifications', icon: Shield },
    { value: 'buyers', label: 'Buyer Verifications', icon: Users },
    { value: 'video', label: 'Video Verifications', icon: Eye },
    { value: 'founding', label: 'Founding Program', icon: Rocket },
    { value: 'blog', label: 'Blog', icon: BookOpen },
    { value: 'audit', label: 'Audit Logs', icon: History },
  ];

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    const adminUser = localStorage.getItem('adminUser');
    
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    
    if (adminUser) {
      setAdmin(JSON.parse(adminUser));
    }
    
    fetchAllData();

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      fetchAllData(true); // silent refresh
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [navigate]);

  // Fetch buyer verifications when section changes or filter changes
  useEffect(() => {
    if (activeSection === 'buyers') {
      fetchBuyerVerifications(buyerVerificationFilter);
    }
  }, [activeSection, buyerVerificationFilter]);

  // Fetch seller verifications when section changes
  useEffect(() => {
    if (activeSection === 'sellers') {
      fetchSellerVerifications();
    }
  }, [activeSection, sellerVerificationFilter]);

  // Fetch video verifications when section changes
  useEffect(() => {
    if (activeSection === 'video') {
      fetchVideoVerifications();
    }
  }, [activeSection]);

  const fetchAllData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      console.log('🔍 Admin Dashboard Debug:');
      console.log('  - API URL:', API);
      console.log('  - Window Origin:', window.location.origin);
      console.log('  - Token exists:', !!token);
      console.log('  - Token preview:', token?.substring(0, 50) + '...');
      
      const [statsRes, companiesRes, inquiriesRes, pipelineRes, auditRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/companies`, { headers }),
        axios.get(`${API}/admin/inquiries`, { headers }),
        axios.get(`${API}/admin/pipeline`, { headers }),
        axios.get(`${API}/admin/audit-logs`, { headers }).catch(() => ({ data: [] }))
      ]);

      console.log('✅ Data received:');
      console.log('  - Stats:', statsRes.data);
      console.log('  - Sellers count:', companiesRes.data?.sellers?.length || 0);
      console.log('  - Buyers count:', companiesRes.data?.buyers?.length || 0);
      console.log('  - Inquiries count:', inquiriesRes.data?.length || 0);
      console.log('  - Debug info:', companiesRes.data?.debug);
      
      if (companiesRes.data?.error) {
        console.error('❌ Companies error:', companiesRes.data.error);
        toast.error('Database error: ' + companiesRes.data.error);
      }

      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setInquiries(inquiriesRes.data);
      setPipeline(pipelineRes.data);
      setAuditLogs(auditRes.data);
      setLastRefreshTime(new Date());

      // Fetch blog posts
      try {
        const blogRes = await axios.get(`${API}/blog/posts?published_only=false`);
        setBlogPosts(blogRes.data.posts || []);
        setBlogCategories(blogRes.data.categories || []);
      } catch (blogError) {
        console.log('Blog fetch error (non-critical):', blogError);
      }

      // Fetch founding program count
      try {
        const foundingRes = await axios.get(`${API}/pricing/founding/count`);
        setFoundingCount(foundingRes.data);
      } catch (foundingError) {
        console.log('Founding count fetch error (non-critical):', foundingError);
      }
    } catch (error) {
      console.error('❌ Fetch error:', error);
      console.error('  - Response status:', error.response?.status);
      console.error('  - Response data:', error.response?.data);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
        toast.error('Session expired. Please login again.');
      } else if (!silent) {
        toast.error('Failed to fetch data: ' + (error.message || 'Unknown error'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch buyer verifications
  const fetchBuyerVerifications = async (status = 'pending') => {
    setBuyerVerificationsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/buyer-verification/admin/review-queue?status=${status}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuyerVerifications(res.data.items || []);
    } catch (error) {
      console.log('Buyer verifications fetch error:', error);
    } finally {
      setBuyerVerificationsLoading(false);
    }
  };

  // Handle buyer verification actions
  const handleApproveBuyer = async (verificationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/buyer-verification/admin/approve/${verificationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Buyer approved successfully');
      fetchBuyerVerifications(buyerVerificationFilter);
      setSelectedBuyerVerification(null);
    } catch (error) {
      toast.error('Failed to approve: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectBuyer = async (verificationId, reason) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/buyer-verification/admin/reject/${verificationId}?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Buyer rejected');
      fetchBuyerVerifications(buyerVerificationFilter);
      setSelectedBuyerVerification(null);
    } catch (error) {
      toast.error('Failed to reject: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle buyer suspend action
  const handleSuspendBuyer = async (verificationId, reason) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/buyer-verification/admin/suspend/${verificationId}?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Buyer suspended');
      fetchBuyerVerifications(buyerVerificationFilter);
      setSelectedBuyerVerification(null);
    } catch (error) {
      toast.error('Failed to suspend: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle buyer unsuspend action
  const handleUnsuspendBuyer = async (verificationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/buyer-verification/admin/unsuspend/${verificationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Buyer unsuspended');
      fetchBuyerVerifications(buyerVerificationFilter);
      setSelectedBuyerVerification(null);
    } catch (error) {
      toast.error('Failed to unsuspend: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Fetch seller verifications (from video verifications or companies.sellers)
  const fetchSellerVerifications = async () => {
    setSellerVerificationsLoading(true);
    try {
      // Use companies.sellers which are already fetched
      // Filter by verification status if needed
      const filtered = companies.sellers?.filter(seller => {
        if (sellerVerificationFilter === 'all') return true;
        if (sellerVerificationFilter === 'pending') return !seller.gstin_verified && !seller.is_verified;
        if (sellerVerificationFilter === 'verified') return seller.is_verified || seller.gstin_verified;
        if (sellerVerificationFilter === 'suspended') return seller.is_suspended;
        return true;
      }) || [];
      setSellerVerifications(filtered);
    } catch (error) {
      console.log('Seller verifications fetch error:', error);
    } finally {
      setSellerVerificationsLoading(false);
    }
  };

  // Fetch video verifications
  const fetchVideoVerifications = async () => {
    setVideoVerificationsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/video-verification/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/video-verification/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVideoVerifications(queueRes.data.verifications || []);
      setVideoStats(statsRes.data);
    } catch (error) {
      console.log('Video verifications fetch error:', error);
    } finally {
      setVideoVerificationsLoading(false);
    }
  };

  const [seedLoading, setSeedLoading] = useState(false);

  // Seed sample products
  const handleSeedProducts = async () => {
    setSeedLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/seed-sample-products`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Success! Added ${response.data.products_created} products and ${response.data.sellers_created} sellers`);
      fetchAllData(true);
    } catch (error) {
      if (error.response?.data?.detail?.includes('already exists')) {
        toast.info('Sample products already exist in the database');
      } else {
        toast.error('Failed to seed products: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSeedLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Adjust founding count
  const handleAdjustFoundingCount = async (action) => {
    setAdjustingCount(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/pricing/admin/founding-count/adjust?action=${action}&value=1`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFoundingCount(prev => ({
          ...prev,
          claimed: response.data.new_count,
          remaining: response.data.remaining
        }));
        toast.success(`Count ${action === 'increment' ? 'increased' : 'decreased'} to ${response.data.new_count}`);
      }
    } catch (error) {
      toast.error('Failed to adjust count: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAdjustingCount(false);
    }
  };

  // Set founding count to specific value
  const handleSetFoundingCount = async (value) => {
    setAdjustingCount(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/pricing/admin/founding-count/adjust?action=set&value=${value}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setFoundingCount(prev => ({
          ...prev,
          claimed: response.data.new_count,
          remaining: response.data.remaining
        }));
        toast.success(`Count set to ${response.data.new_count}`);
      }
    } catch (error) {
      toast.error('Failed to set count: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAdjustingCount(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
    toast.info('Logged out successfully');
  };

  // Blog Management Functions
  const handleOpenBlogDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setBlogForm({
        title: post.title,
        slug: post.slug,
        category: post.category,
        excerpt: post.excerpt,
        content: post.content,
        cover_image: post.cover_image || '',
        tags: post.tags?.join(', ') || '',
        published: post.published
      });
    } else {
      setEditingPost(null);
      setBlogForm({
        title: '',
        slug: '',
        category: 'export-tips',
        excerpt: '',
        content: '',
        cover_image: '',
        tags: '',
        published: true
      });
    }
    setShowBlogDialog(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleBlogFormChange = (field, value) => {
    setBlogForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'title' && !editingPost) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleSaveBlogPost = async () => {
    if (!blogForm.title || !blogForm.content) {
      toast.error('Title and content are required');
      return;
    }

    setActionLoading(true);
    try {
      const postData = {
        ...blogForm,
        tags: blogForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, postData);
        toast.success('Post updated successfully!');
      } else {
        await axios.post(`${API}/blog/posts`, postData);
        toast.success('Post created successfully!');
      }

      setShowBlogDialog(false);
      fetchAllData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBlogPost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/blog/posts/${postId}`);
      toast.success('Post deleted successfully!');
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // Admin IEC Actions
  const handleViewSeller = (seller) => {
    setSelectedSeller(seller);
    setShowSellerDialog(true);
  };

  const handleApproveIEC = async (sellerId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/seller/${sellerId}/approve-iec`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('IEC approved successfully!');
      setShowSellerDialog(false);
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to approve IEC');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectIEC = async () => {
    if (!selectedSeller) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/seller/${selectedSeller.id}/reject-iec`, 
        { reason: rejectReason || 'IEC verification failed' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('IEC rejected');
      setShowRejectDialog(false);
      setShowSellerDialog(false);
      setRejectReason('');
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to reject IEC');
    } finally {
      setActionLoading(false);
    }
  };

  // GSTIN Approve/Reject with Email Notifications (Task 3, 4, 5)
  const handleApproveGSTIN = async (sellerId, sendEmail = true) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/seller/${sellerId}/approve-gstin`, 
        { send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.email_sent 
        ? 'GSTIN approved & email sent!' 
        : 'GSTIN approved successfully!');
      setShowSellerDialog(false);
      fetchAllData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve GSTIN');
    } finally {
      setActionLoading(false);
    }
  };

  const [showGstinRejectDialog, setShowGstinRejectDialog] = useState(false);
  const [gstinRejectReason, setGstinRejectReason] = useState('');

  const handleRejectGSTIN = async () => {
    if (!selectedSeller) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/seller/${selectedSeller.id}/reject-gstin`, 
        { reason: gstinRejectReason || 'GSTIN verification failed' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('GSTIN rejected');
      setShowGstinRejectDialog(false);
      setShowSellerDialog(false);
      setGstinRejectReason('');
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to reject GSTIN');
    } finally {
      setActionLoading(false);
    }
  };

  // IEC Approve with Email Notification (Task 5)
  const handleApproveIECWithNotification = async (sellerId, sendEmail = true) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/seller/${sellerId}/approve-iec-with-notification`, 
        { send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.email_sent 
        ? 'IEC approved & email sent!' 
        : 'IEC approved successfully!');
      setShowSellerDialog(false);
      fetchAllData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve IEC');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Seller (Task 6)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState(null);

  const handleDeleteSeller = async () => {
    if (!sellerToDelete) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API}/admin/seller/${sellerToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Seller "${sellerToDelete.company_name}" deleted successfully`);
      setShowDeleteDialog(false);
      setSellerToDelete(null);
      setShowSellerDialog(false);
      fetchAllData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete seller');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk GSTIN Verification (Task 1 & 2)
  const [bulkVerifying, setBulkVerifying] = useState(false);

  const handleBulkVerifyGSTIN = async () => {
    setBulkVerifying(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/admin/bulk-verify-gstin`, 
        { send_emails: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      const { verified, failed, results } = response.data;
      toast.success(`Verified ${verified} GSTINs, ${failed} failed`);
      
      // Show details
      if (results && results.length > 0) {
        results.forEach(r => {
          if (r.status === 'verified') {
            console.log(`✓ Verified: ${r.name} (${r.email}) - GSTIN: ${r.gstin}`);
          }
        });
      }
      
      fetchAllData(true);
    } catch (error) {
      toast.error('Bulk verification failed');
    } finally {
      setBulkVerifying(false);
    }
  };

  const handleSuspendSeller = async () => {
    if (!selectedSeller) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/seller/${selectedSeller.id}/suspend`,
        { reason: suspendReason || 'Account suspended by admin' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Seller suspended');
      setShowSuspendDialog(false);
      setShowSellerDialog(false);
      setSuspendReason('');
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to suspend seller');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendSeller = async (sellerId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/seller/${sellerId}/unsuspend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Seller unsuspended');
      fetchAllData(true);
    } catch (error) {
      toast.error('Failed to unsuspend seller');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">GlobalVyapar Admin</h1>
                <p className="text-xs text-slate-400">Control Panel</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {lastRefreshTime && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Auto-refresh: {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
              <span className="text-sm text-slate-400">
                Welcome, <span className="text-white font-medium">{admin?.email}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Sellers</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_sellers || 0}</p>
                  <p className="text-xs text-green-400 flex items-center gap-1 mt-1">
                    <ArrowUpRight className="h-3 w-3" />
                    +{stats?.new_sellers_this_week || 0} this week
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <Building2 className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Buyers</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_buyers || 0}</p>
                  <p className="text-xs text-blue-400 flex items-center gap-1 mt-1">
                    <ArrowUpRight className="h-3 w-3" />
                    +{stats?.new_buyers_this_week || 0} this week
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Inquiries</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_inquiries || 0}</p>
                  <p className="text-xs text-purple-400 flex items-center gap-1 mt-1">
                    <MessageSquare className="h-3 w-3" />
                    {stats?.active_inquiries || 0} active
                  </p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Deals Completed</p>
                  <p className="text-3xl font-bold text-white">{stats?.deals_completed || 0}</p>
                  <p className="text-xs text-amber-400 flex items-center gap-1 mt-1">
                    <DollarSign className="h-3 w-3" />
                    {stats?.deals_in_progress || 0} in progress
                  </p>
                </div>
                <div className="p-3 bg-amber-500/20 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-500/20 rounded-lg">
                  <Package className="h-6 w-6 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Products</p>
                  <p className="text-2xl font-bold text-white">{stats?.total_products || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-pink-500/20 rounded-lg">
                  <Eye className="h-6 w-6 text-pink-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Page Views (Today)</p>
                  <p className="text-2xl font-bold text-white">{stats?.page_views_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-cyan-500/20 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Conversion Rate</p>
                  <p className="text-2xl font-bold text-white">{stats?.conversion_rate || '0'}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Seed Products Card */}
          <Card className="bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border-emerald-700/50">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center text-center">
                <p className="text-sm text-emerald-300 mb-2">Add Sample Products</p>
                <Button
                  onClick={handleSeedProducts}
                  disabled={seedLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {seedLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Seeding...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Seed 12 Products
                    </>
                  )}
                </Button>
                <p className="text-xs text-emerald-400/70 mt-2">Adds sample listings to marketplace</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section Navigation - Clean Dropdown */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Select value={activeSection} onValueChange={setActiveSection}>
              <SelectTrigger className="w-[250px] bg-slate-800 border-slate-700 text-white">
                <div className="flex items-center gap-2">
                  {sectionOptions.find(s => s.value === activeSection)?.icon && (
                    <span className="text-indigo-400">
                      {(() => {
                        const Icon = sectionOptions.find(s => s.value === activeSection)?.icon;
                        return Icon ? <Icon className="h-4 w-4" /> : null;
                      })()}
                    </span>
                  )}
                  <SelectValue placeholder="Select section" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {sectionOptions.map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className="text-slate-200 focus:bg-slate-700 focus:text-white"
                  >
                    <div className="flex items-center gap-2">
                      <option.icon className="h-4 w-4 text-indigo-400" />
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex-1 h-px bg-slate-700"></div>
            
            <span className="text-sm text-slate-500">
              {sectionOptions.find(s => s.value === activeSection)?.label}
            </span>
          </div>
        </div>

        {/* Analytics Section - Page Views Graph */}
        {activeSection === 'analytics' && (
          <div className="space-y-6">
            {/* Page Views Chart */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-pink-400" />
                  Page Views - Last 7 Days
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Visitor activity on your platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {stats?.daily_visitors && stats.daily_visitors.length > 0 ? (
                    <div className="flex items-end justify-between h-full gap-2 pt-4">
                      {stats.daily_visitors.map((day, index) => {
                        const maxViews = Math.max(...stats.daily_visitors.map(d => d.views || 0), 1);
                        const heightPercent = ((day.views || 0) / maxViews) * 100;
                        const date = new Date(day._id);
                        const dayName = date.toLocaleDateString('en', { weekday: 'short' });
                        
                        return (
                          <div key={index} className="flex flex-col items-center flex-1">
                            <span className="text-xs text-slate-400 mb-1">{day.views || 0}</span>
                            <div 
                              className="w-full bg-gradient-to-t from-indigo-600 to-purple-500 rounded-t transition-all duration-500"
                              style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '8px' }}
                            ></div>
                            <span className="text-xs text-slate-500 mt-2">{dayName}</span>
                            <span className="text-xs text-slate-600">{day._id?.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Globe className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">No visitor data yet</p>
                        <p className="text-sm text-slate-500 mt-1">Page views will appear here once tracking is active</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Page Views by Page */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  Top Pages
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.page_views_by_page && stats.page_views_by_page.length > 0 ? (
                  <div className="space-y-3">
                    {stats.page_views_by_page.map((page, index) => {
                      const maxCount = stats.page_views_by_page[0]?.count || 1;
                      const widthPercent = (page.count / maxCount) * 100;
                      
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-slate-400 w-32 text-sm truncate">{page._id || '/'}</span>
                          <div className="flex-1 h-6 bg-slate-700 rounded overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-600 to-blue-500 flex items-center justify-end pr-2"
                              style={{ width: `${widthPercent}%` }}
                            >
                              <span className="text-xs text-white font-medium">{page.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-4">No page data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Companies Section */}
        {activeSection === 'companies' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Sellers */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-400" />
                      Registered Sellers
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      {companies.total_sellers || companies.sellers?.length || 0} sellers registered
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleBulkVerifyGSTIN}
                    disabled={bulkVerifying}
                    className="bg-teal-600 hover:bg-teal-700 text-xs"
                    data-testid="bulk-verify-gstin-btn"
                  >
                    {bulkVerifying ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="h-3 w-3 mr-1" />
                    )}
                    Bulk Verify GSTIN
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Company</TableHead>
                          <TableHead className="text-slate-400">Contact</TableHead>
                          <TableHead className="text-slate-400">Location</TableHead>
                          <TableHead className="text-slate-400">GSTIN</TableHead>
                          <TableHead className="text-slate-400">IEC</TableHead>
                          <TableHead className="text-slate-400">Products</TableHead>
                          <TableHead className="text-slate-400">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companies.sellers?.map((seller) => (
                          <TableRow key={seller.id} className="border-slate-700 hover:bg-slate-700/50">
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-white">{seller.company_name || seller.full_name}</p>
                                  {/* Fully Verified Badge */}
                                  <FullyVerifiedBadge 
                                    phoneVerified={seller.phone_verified}
                                    gstinVerified={seller.gstin_verified}
                                    iecStatus={seller.iec_status}
                                    size="small"
                                    variant="compact"
                                    showLabel={false}
                                  />
                                  {seller.is_suspended && (
                                    <Badge className="bg-red-500/20 text-red-400 text-[10px] px-1">Suspended</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-400">{seller.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <PhoneVerifiedBadge verified={seller.phone_verified} size="small" />
                                <div className="text-xs">
                                  <p className="text-slate-300">{seller.phone || 'No phone'}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300 text-sm">
                              {seller.district || seller.city || seller.country || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <GSTVerifiedBadge verified={seller.gstin_verified} size="small" />
                                <div className="text-xs">
                                  <p className="text-slate-300 font-mono text-[10px]">{seller.gstin ? seller.gstin.substring(0, 10) + '...' : 'N/A'}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <IECVerifiedBadge status={seller.iec_status} size="small" />
                                <div className="text-xs">
                                  <p className="text-slate-300 font-mono">{seller.iec_code || 'N/A'}</p>
                                  {seller.iec_status === 'pending' && (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-[10px] px-1">Pending</Badge>
                                  )}
                                  {seller.iec_status === 'rejected' && (
                                    <Badge className="bg-red-500/20 text-red-400 text-[10px] px-1">Rejected</Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{seller.products_count || 0}</TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewSeller(seller)}
                                className="text-xs border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!companies.sellers || companies.sellers.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-slate-400 py-8">
                              No sellers registered yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Buyers */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-400" />
                    Registered Buyers
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    {companies.buyers?.length || 0} buyers registered
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Company</TableHead>
                          <TableHead className="text-slate-400">Contact</TableHead>
                          <TableHead className="text-slate-400">Country</TableHead>
                          <TableHead className="text-slate-400">Joined</TableHead>
                          <TableHead className="text-slate-400">Inquiries</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companies.buyers?.map((buyer) => (
                          <TableRow key={buyer.id} className="border-slate-700 hover:bg-slate-700/50">
                            <TableCell>
                              <div>
                                <p className="font-medium text-white">{buyer.company_name || buyer.full_name}</p>
                                <p className="text-xs text-slate-400">{buyer.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs">
                                <p className="text-slate-300">{buyer.phone || 'No phone'}</p>
                                {buyer.phone_verified ? (
                                  <Badge className="bg-green-500/20 text-green-400 text-[10px] px-1">Verified</Badge>
                                ) : (
                                  <Badge className="bg-slate-500/20 text-slate-400 text-[10px] px-1">Unverified</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{buyer.country || 'N/A'}</TableCell>
                            <TableCell className="text-slate-300 text-sm">{formatDate(buyer.created_at)}</TableCell>
                            <TableCell className="text-slate-300">{buyer.inquiry_count || 0}</TableCell>
                          </TableRow>
                        ))}
                        {(!companies.buyers || companies.buyers.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-slate-400 py-8">
                              No buyers registered yet
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
        )}

        {/* Inquiries Section */}
        {activeSection === 'inquiries' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">All Inquiries</CardTitle>
              <CardDescription className="text-slate-400">
                Complete list of inquiries across the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Product</TableHead>
                      <TableHead className="text-slate-400">Buyer</TableHead>
                      <TableHead className="text-slate-400">Seller</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Close Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inquiries?.map((inquiry) => (
                        <TableRow key={inquiry.id} className="border-slate-700">
                          <TableCell className="font-medium text-white">{inquiry.product_title}</TableCell>
                          <TableCell className="text-slate-300">{inquiry.buyer_name}</TableCell>
                          <TableCell className="text-slate-300">{inquiry.seller_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              inquiry.status === 'closed' 
                                ? 'bg-slate-500/20 text-slate-400' 
                                : inquiry.status === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }>
                              {inquiry.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">{formatDate(inquiry.created_at)}</TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {inquiry.close_reason || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!inquiries || inquiries.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                            No inquiries yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
        )}

        {/* Pipeline Section */}
        {activeSection === 'pipeline' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Deal Pipeline</CardTitle>
              <CardDescription className="text-slate-400">
                Track deals through different stages
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Pipeline Summary */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                {[
                  { stage: 'new', label: 'New', color: 'bg-blue-500' },
                  { stage: 'contacted', label: 'Contacted', color: 'bg-purple-500' },
                  { stage: 'negotiation', label: 'Negotiation', color: 'bg-amber-500' },
                  { stage: 'proposal', label: 'Proposal', color: 'bg-orange-500' },
                  { stage: 'closed_won', label: 'Won', color: 'bg-green-500' },
                  { stage: 'closed_lost', label: 'Lost', color: 'bg-red-500' }
                ].map((item) => (
                  <div key={item.stage} className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <div className={`w-3 h-3 ${item.color} rounded-full mx-auto mb-2`}></div>
                      <p className="text-xs text-slate-400">{item.label}</p>
                      <p className="text-xl font-bold text-white">
                        {pipeline?.filter(p => p.stage === item.stage).length || 0}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Pipeline Table */}
                <div className="max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Product</TableHead>
                        <TableHead className="text-slate-400">Buyer</TableHead>
                        <TableHead className="text-slate-400">Seller</TableHead>
                        <TableHead className="text-slate-400">Stage</TableHead>
                        <TableHead className="text-slate-400">Est. Value</TableHead>
                        <TableHead className="text-slate-400">Last Contact</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pipeline?.map((lead) => (
                        <TableRow key={lead.id} className="border-slate-700">
                          <TableCell className="font-medium text-white">{lead.product_title}</TableCell>
                          <TableCell className="text-slate-300">{lead.buyer_name}</TableCell>
                          <TableCell className="text-slate-300">{lead.seller_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              lead.stage === 'closed_won' ? 'bg-green-500/20 text-green-400' :
                              lead.stage === 'closed_lost' ? 'bg-red-500/20 text-red-400' :
                              lead.stage === 'negotiation' ? 'bg-amber-500/20 text-amber-400' :
                              lead.stage === 'proposal' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-blue-500/20 text-blue-400'
                            }>
                              {lead.stage?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {lead.estimated_value ? formatCurrency(lead.estimated_value) : '-'}
                          </TableCell>
                          <TableCell className="text-slate-300">{formatDate(lead.last_contact)}</TableCell>
                        </TableRow>
                      ))}
                      {(!pipeline || pipeline.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                            No leads in pipeline
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
        )}

        {/* Buyer Verifications Section */}
        {activeSection === 'buyers' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-400" />
                Buyer Verifications
              </CardTitle>
              <CardDescription className="text-slate-400">
                Review and approve international buyer applications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter Tabs */}
              <div className="flex gap-2 flex-wrap">
                {['pending', 'approved', 'rejected', 'suspended', 'more_info_needed'].map(status => (
                  <Button
                    key={status}
                    variant={buyerVerificationFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBuyerVerificationFilter(status)}
                    className={buyerVerificationFilter === status 
                      ? 'bg-blue-600 text-white' 
                      : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                    data-testid={`buyer-filter-${status}`}
                  >
                    {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Button>
                ))}
              </div>

              {/* Verifications List */}
              {buyerVerificationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                </div>
              ) : buyerVerifications.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No {buyerVerificationFilter} buyer verifications</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {buyerVerifications.map(verification => (
                    <div 
                      key={verification.verification_id}
                      className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer"
                      onClick={() => setSelectedBuyerVerification(verification)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg
                            ${verification.email_type === 'business' ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                            {verification.country_code === 'US' ? '🇺🇸' :
                             verification.country_code === 'GB' ? '🇬🇧' :
                             verification.country_code === 'AE' ? '🇦🇪' :
                             verification.country_code === 'CN' ? '🇨🇳' :
                             verification.country_code === 'DE' ? '🇩🇪' : '🌍'}
                          </div>
                          <div>
                            <p className="text-white font-medium">{verification.company_name}</p>
                            <p className="text-sm text-slate-400">{verification.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            verification.email_type === 'business' 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }>
                            {verification.email_type === 'business' ? 'Business Email' : 'Free Email'}
                          </Badge>
                          {verification.fraud_score !== undefined && (
                            <p className={`text-xs mt-1 ${
                              verification.fraud_score >= 30 ? 'text-emerald-400' :
                              verification.fraud_score >= 0 ? 'text-amber-400' : 'text-red-400'
                            }`}>
                              Fraud Score: {verification.fraud_score}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                        <span>Country: {verification.country_code}</span>
                        <span>•</span>
                        <span>Submitted: {new Date(verification.submitted_at).toLocaleDateString()}</span>
                        {verification.monthly_volume && (
                          <>
                            <span>•</span>
                            <span className="text-emerald-400">Volume: {verification.monthly_volume}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Verification Detail Modal */}
              {selectedBuyerVerification && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h3 className="text-xl font-bold text-white">{selectedBuyerVerification.company_name}</h3>
                          <p className="text-slate-400">{selectedBuyerVerification.email}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedBuyerVerification(null)}
                          className="text-slate-400"
                        >
                          ✕
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Country</p>
                          <p className="text-white">{selectedBuyerVerification.country_code}</p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Email Type</p>
                          <p className={selectedBuyerVerification.email_type === 'business' ? 'text-emerald-400' : 'text-amber-400'}>
                            {selectedBuyerVerification.email_type}
                          </p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Fraud Score</p>
                          <p className={
                            selectedBuyerVerification.fraud_score >= 30 ? 'text-emerald-400' :
                            selectedBuyerVerification.fraud_score >= 0 ? 'text-amber-400' : 'text-red-400'
                          }>
                            {selectedBuyerVerification.fraud_score ?? 'N/A'}
                          </p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">Priority</p>
                          <p className="text-white">{selectedBuyerVerification.priority === 0 ? 'High' : 'Normal'}</p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {buyerVerificationFilter === 'pending' && (
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleApproveBuyer(selectedBuyerVerification.verification_id)}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="approve-buyer-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => {
                              const reason = prompt('Enter rejection reason:');
                              if (reason) handleRejectBuyer(selectedBuyerVerification.verification_id, reason);
                            }}
                            variant="outline"
                            className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid="reject-buyer-btn"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            onClick={() => {
                              const reason = prompt('Enter suspension reason:');
                              if (reason) handleSuspendBuyer(selectedBuyerVerification.verification_id, reason);
                            }}
                            variant="outline"
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                            data-testid="suspend-buyer-btn"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Suspend
                          </Button>
                        </div>
                      )}

                      {/* Actions for approved buyers */}
                      {buyerVerificationFilter === 'approved' && (
                        <div className="flex gap-3">
                          <Button
                            onClick={() => {
                              const reason = prompt('Enter suspension reason:');
                              if (reason) handleSuspendBuyer(selectedBuyerVerification.verification_id, reason);
                            }}
                            variant="outline"
                            className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                            data-testid="suspend-approved-buyer-btn"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Suspend
                          </Button>
                        </div>
                      )}

                      {/* Actions for suspended buyers */}
                      {buyerVerificationFilter === 'suspended' && selectedBuyerVerification.is_suspended && (
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleUnsuspendBuyer(selectedBuyerVerification.verification_id)}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="unsuspend-buyer-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Unsuspend
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Founding Program Section */}
        {activeSection === 'founding' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Rocket className="h-5 w-5 text-amber-400" />
                Founding Exporter Program
              </CardTitle>
              <CardDescription className="text-slate-400">
                Manage the founding exporter counter displayed on the website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Count Display */}
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-6">
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Current Count</p>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleAdjustFoundingCount('decrement')}
                      disabled={adjustingCount || foundingCount.claimed <= 0}
                      className="h-14 w-14 rounded-full border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                      data-testid="founding-decrement-btn"
                    >
                      <Minus className="h-6 w-6" />
                    </Button>
                    
                    <div className="text-center min-w-[150px]">
                      <span className="text-5xl font-bold text-white">
                        {adjustingCount ? '...' : foundingCount.claimed}
                      </span>
                      <span className="text-2xl text-slate-400">/{foundingCount.total_slots}</span>
                      <p className="text-amber-400 text-sm mt-1">spots claimed</p>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleAdjustFoundingCount('increment')}
                      disabled={adjustingCount || foundingCount.claimed >= foundingCount.total_slots}
                      className="h-14 w-14 rounded-full border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                      data-testid="founding-increment-btn"
                    >
                      <Plus className="h-6 w-6" />
                    </Button>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-4 w-full max-w-md mx-auto">
                    <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-500"
                        style={{ width: `${(foundingCount.claimed / foundingCount.total_slots) * 100}%` }}
                      />
                    </div>
                    <p className="text-slate-400 text-sm mt-2">
                      {foundingCount.remaining} spots remaining
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Set Buttons */}
              <div className="space-y-3">
                <p className="text-slate-400 text-sm">Quick Set</p>
                <div className="flex flex-wrap gap-2">
                  {[10, 25, 40, 50, 75, 100, 150, 200].map(num => (
                    <Button
                      key={num}
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetFoundingCount(num)}
                      disabled={adjustingCount}
                      className={`border-slate-600 hover:bg-slate-700 ${foundingCount.claimed === num ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'text-slate-300'}`}
                    >
                      {num}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
                <h4 className="text-white font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  How This Works
                </h4>
                <ul className="text-slate-400 text-sm space-y-1 list-disc list-inside">
                  <li>This counter appears on the Landing page and Pricing page</li>
                  <li>Shows as &quot;X/500 claimed&quot; in the Founding Exporter banner</li>
                  <li>Updates immediately on the frontend when you change it</li>
                  <li>Total exporters count: <span className="text-white font-medium">{foundingCount.total_exporters || 0}</span></li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Blog Management Section */}
        {activeSection === 'blog' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-teal-400" />
                  Blog Posts
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Manage your blog articles
                </CardDescription>
              </div>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => handleOpenBlogDialog()}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Post
              </Button>
            </CardHeader>
              <CardContent>
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Title</TableHead>
                        <TableHead className="text-slate-400">Category</TableHead>
                        <TableHead className="text-slate-400">Status</TableHead>
                        <TableHead className="text-slate-400">Views</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPosts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                            No blog posts yet. Create your first post!
                          </TableCell>
                        </TableRow>
                      ) : (
                        blogPosts.map((post) => (
                          <TableRow key={post.id} className="border-slate-700">
                            <TableCell>
                              <div>
                                <p className="text-white font-medium">{post.title}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[250px]">{post.excerpt}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="capitalize">
                                {post.category?.replace('-', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {post.published ? (
                                <Badge className="bg-green-500/20 text-green-400">Published</Badge>
                              ) : (
                                <Badge className="bg-yellow-500/20 text-yellow-400">Draft</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {post.views || 0}
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {new Date(post.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-white"
                                  onClick={() => handleOpenBlogDialog(post)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => handleDeleteBlogPost(post.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
        )}

        {/* Audit Logs Section */}
        {activeSection === 'audit' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="h-5 w-5" />
                Admin Activity Log
              </CardTitle>
              <CardDescription className="text-slate-400">
                Track all admin actions for security and compliance
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-12 w-12 mx-auto text-slate-600 mb-4" />
                  <p className="text-slate-400">No audit logs yet</p>
                  <p className="text-xs text-slate-500">Actions will be logged as you use the admin panel</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {auditLogs.map((log, idx) => (
                      <div 
                        key={log.id || idx} 
                        className="flex items-start gap-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                      >
                        <div className={`p-2 rounded-full ${
                          log.action.includes('success') ? 'bg-green-500/20' :
                          log.action.includes('failed') ? 'bg-red-500/20' :
                          log.action.includes('suspended') ? 'bg-amber-500/20' :
                          'bg-blue-500/20'
                        }`}>
                          {log.action.includes('success') ? <CheckCircle className="h-4 w-4 text-green-400" /> :
                           log.action.includes('failed') ? <XCircle className="h-4 w-4 text-red-400" /> :
                           log.action.includes('suspended') ? <Ban className="h-4 w-4 text-amber-400" /> :
                           <History className="h-4 w-4 text-blue-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium">
                            {log.action.replace(/_/g, ' ').toUpperCase()}
                          </p>
                          <p className="text-xs text-slate-400">
                            {log.admin_email}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(' • ')}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}

        {/* Seller Verifications Section */}
        {activeSection === 'sellers' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-400" />
                Seller Verifications
              </CardTitle>
              <CardDescription className="text-slate-400">
                Review and manage seller verification status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filter Tabs */}
              <div className="flex gap-2 flex-wrap">
                {['all', 'pending', 'verified', 'suspended'].map(status => (
                  <Button
                    key={status}
                    variant={sellerVerificationFilter === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSellerVerificationFilter(status)}
                    className={sellerVerificationFilter === status 
                      ? 'bg-green-600 text-white' 
                      : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Sellers List */}
              {sellerVerificationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-green-400" />
                </div>
              ) : sellerVerifications.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No {sellerVerificationFilter} seller verifications</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {sellerVerifications.map(seller => (
                    <div 
                      key={seller.id}
                      className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors cursor-pointer"
                      onClick={() => handleViewSeller(seller)}
                      data-testid={`seller-verification-item-${seller.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            seller.is_suspended ? 'bg-red-500/20' :
                            seller.is_verified ? 'bg-green-500/20' :
                            seller.gstin_verified ? 'bg-emerald-500/20' :
                            'bg-slate-500/20'
                          }`}>
                            <Building2 className={`h-5 w-5 ${
                              seller.is_suspended ? 'text-red-400' :
                              seller.is_verified ? 'text-green-400' :
                              seller.gstin_verified ? 'text-emerald-400' :
                              'text-slate-400'
                            }`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{seller.company_name || seller.full_name}</p>
                            <p className="text-sm text-slate-400">{seller.email}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {seller.is_suspended && (
                            <Badge className="bg-red-500/20 text-red-400">Suspended</Badge>
                          )}
                          {seller.is_verified && (
                            <Badge className="bg-green-500/20 text-green-400">Verified</Badge>
                          )}
                          {seller.gstin_verified && !seller.is_verified && (
                            <Badge className="bg-emerald-500/20 text-emerald-400">GSTIN Verified</Badge>
                          )}
                          {!seller.is_verified && !seller.gstin_verified && !seller.is_suspended && (
                            <Badge className="bg-amber-500/20 text-amber-400">Pending</Badge>
                          )}
                          {seller.video_verification_status && (
                            <Badge className={
                              seller.video_verification_status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                              seller.video_verification_status === 'pending_review' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-slate-500/20 text-slate-400'
                            }>
                              Video: {seller.video_verification_status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                        <span>Country: {seller.country || seller.country_code || 'IN'}</span>
                        <span>•</span>
                        <span>Products: {seller.products_count || 0}</span>
                        <span>•</span>
                        <span>Joined: {new Date(seller.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Video Verifications Section */}
        {activeSection === 'video' && (
          <AdminVideoVerificationQueue />
        )}
      </main>

      {/* Seller Detail Dialog */}
      <Dialog open={showSellerDialog} onOpenChange={setShowSellerDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Building2 className="h-5 w-5 text-green-400" />
              {selectedSeller?.company_name || selectedSeller?.full_name}
              {selectedSeller?.is_suspended && (
                <Badge className="bg-red-500/20 text-red-400 ml-2">Suspended</Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Seller details and verification status
            </DialogDescription>
          </DialogHeader>
          
          {selectedSeller && (
            <div className="space-y-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Email</Label>
                  <p className="text-white flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-500" />
                    {selectedSeller.email}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Phone</Label>
                  <p className="text-white flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-500" />
                    {selectedSeller.phone || 'Not provided'}
                    {selectedSeller.phone_verified && (
                      <Badge className="bg-green-500/20 text-green-400 text-[10px]">Verified</Badge>
                    )}
                  </p>
                  {/* Phone Visibility Toggle */}
                  {selectedSeller.phone && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        id="phone-visibility"
                        checked={selectedSeller.phone_public !== false}
                        onChange={async (e) => {
                          try {
                            const token = localStorage.getItem('adminToken');
                            await axios.post(`${API}/admin/seller/${selectedSeller.id}/toggle-phone-visibility`, 
                              { phone_public: e.target.checked },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setSelectedSeller(prev => ({ ...prev, phone_public: e.target.checked }));
                            toast.success(`Phone ${e.target.checked ? 'visible' : 'hidden'} publicly`);
                          } catch (error) {
                            toast.error('Failed to update phone visibility');
                          }
                        }}
                        className="rounded border-slate-600 bg-slate-700 text-primary focus:ring-primary"
                        data-testid="phone-visibility-toggle"
                      />
                      <Label htmlFor="phone-visibility" className="text-xs text-slate-400 cursor-pointer">
                        Show phone publicly on marketplace
                      </Label>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">District</Label>
                  <p className="text-white flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    {selectedSeller.district || 'Not provided'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Joined</Label>
                  <p className="text-white">{formatDate(selectedSeller.created_at)}</p>
                </div>
              </div>

              {/* Verification Section */}
              <div className="border-t border-slate-700 pt-4 space-y-4">
                <h4 className="font-medium text-lg">Verification Status</h4>
                
                {/* GSTIN - with Approve/Reject buttons */}
                <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium">GSTIN</p>
                      <p className="text-xs text-slate-400 font-mono">{selectedSeller.gstin || 'Not provided'}</p>
                      {selectedSeller.gstin_status === 'rejected' && selectedSeller.gstin_rejection_reason && (
                        <p className="text-xs text-red-400 mt-1">Reason: {selectedSeller.gstin_rejection_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSeller.gstin_verified || selectedSeller.gstin_status === 'approved' ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </Badge>
                    ) : selectedSeller.gstin_status === 'rejected' ? (
                      <>
                        <Badge className="bg-red-500/20 text-red-400">
                          <XCircle className="h-3 w-3 mr-1" />
                          Rejected
                        </Badge>
                        <Button
                          size="sm"
                          onClick={() => handleApproveGSTIN(selectedSeller.id, true)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-xs"
                        >
                          Re-approve
                        </Button>
                      </>
                    ) : selectedSeller.gstin ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApproveGSTIN(selectedSeller.id, true)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-xs"
                          data-testid="approve-gstin-btn"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve & Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowGstinRejectDialog(true)}
                          disabled={actionLoading}
                          className="border-red-500 text-red-400 hover:bg-red-500/20 text-xs"
                          data-testid="reject-gstin-btn"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-400">Not Submitted</Badge>
                    )}
                  </div>
                </div>

                {/* IEC - with Email Notification */}
                <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Ship className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium">IEC Code</p>
                      <p className="text-xs text-slate-400 font-mono">{selectedSeller.iec_code || 'Not provided'}</p>
                      {selectedSeller.iec_rejection_reason && (
                        <p className="text-xs text-red-400 mt-1">Reason: {selectedSeller.iec_rejection_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedSeller.iec_status === 'approved' ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Approved
                      </Badge>
                    ) : selectedSeller.iec_status === 'pending' || selectedSeller.iec_code ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApproveIECWithNotification(selectedSeller.id, true)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-xs"
                          data-testid="approve-iec-btn"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve & Email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowRejectDialog(true)}
                          disabled={actionLoading}
                          className="border-red-500 text-red-400 hover:bg-red-500/20 text-xs"
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </>
                    ) : selectedSeller.iec_status === 'rejected' ? (
                      <Badge className="bg-red-500/20 text-red-400">
                        <XCircle className="h-3 w-3 mr-1" />
                        Rejected
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-400">Not Submitted</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Account Actions */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="font-medium text-lg mb-3">Account Actions</h4>
                <div className="flex gap-2">
                  {selectedSeller.is_suspended ? (
                    <Button
                      onClick={() => handleUnsuspendSeller(selectedSeller.id)}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Unsuspend Account
                    </Button>
                  ) : (
                    <Button
                      variant="destructive"
                      onClick={() => setShowSuspendDialog(true)}
                      disabled={actionLoading}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Suspend Account
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500/20"
                    onClick={() => {
                      setSellerToDelete(selectedSeller);
                      setShowDeleteDialog(true);
                    }}
                    disabled={actionLoading}
                    data-testid="delete-seller-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Seller
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject IEC Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Reject IEC
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Provide a reason for rejecting this IEC code
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-400">Rejection Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectIEC} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject IEC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Account Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Ban className="h-5 w-5" />
              Suspend Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will suspend the seller&apos;s account. They will not be able to access the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-400">Suspension Reason</Label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason for suspension..."
              className="mt-2 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSuspendSeller} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Suspend Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GSTIN Reject Dialog */}
      <Dialog open={showGstinRejectDialog} onOpenChange={setShowGstinRejectDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Reject GSTIN
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Provide a reason for rejecting this GSTIN. The seller will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-400">Rejection Reason</Label>
            <Textarea
              value={gstinRejectReason}
              onChange={(e) => setGstinRejectReason(e.target.value)}
              placeholder="e.g., GSTIN not found in government database..."
              className="mt-2 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGstinRejectDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectGSTIN} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject GSTIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Seller Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Seller
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete the seller and all associated data (products, inquiries, leads).
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {sellerToDelete && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-white font-medium">{sellerToDelete.company_name}</p>
                <p className="text-slate-400 text-sm">{sellerToDelete.email}</p>
                <p className="text-slate-400 text-sm mt-2">
                  Products: {sellerToDelete.products_count || 0}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSeller} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blog Post Dialog */}
      <Dialog open={showBlogDialog} onOpenChange={setShowBlogDialog}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-teal-400" />
              {editingPost ? 'Edit Blog Post' : 'Create New Post'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingPost ? 'Update your blog post details' : 'Create a new blog article for your audience'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-300">Title *</Label>
              <Input
                value={blogForm.title}
                onChange={(e) => handleBlogFormChange('title', e.target.value)}
                placeholder="Enter post title..."
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-title-input"
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label className="text-slate-300">Slug</Label>
              <Input
                value={blogForm.slug}
                onChange={(e) => handleBlogFormChange('slug', e.target.value)}
                placeholder="post-url-slug"
                className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                data-testid="blog-slug-input"
              />
              <p className="text-xs text-slate-500">URL: /blog/{blogForm.slug || 'post-slug'}</p>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={blogForm.category} 
                onValueChange={(value) => handleBlogFormChange('category', value)}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="blog-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="export-tips">Export Tips</SelectItem>
                  <SelectItem value="global-markets">Global Markets</SelectItem>
                  <SelectItem value="platform-updates">Platform Updates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Excerpt */}
            <div className="space-y-2">
              <Label className="text-slate-300">Excerpt</Label>
              <Textarea
                value={blogForm.excerpt}
                onChange={(e) => handleBlogFormChange('excerpt', e.target.value)}
                placeholder="A short summary of your post..."
                className="bg-slate-800 border-slate-700 text-white h-20"
                data-testid="blog-excerpt-input"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label className="text-slate-300">Content * (Markdown supported)</Label>
              <Textarea
                value={blogForm.content}
                onChange={(e) => handleBlogFormChange('content', e.target.value)}
                placeholder="Write your blog post content here... (Markdown supported)"
                className="bg-slate-800 border-slate-700 text-white h-64 font-mono text-sm"
                data-testid="blog-content-input"
              />
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label className="text-slate-300">Cover Image URL</Label>
              <Input
                value={blogForm.cover_image}
                onChange={(e) => handleBlogFormChange('cover_image', e.target.value)}
                placeholder="https://images.unsplash.com/..."
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-cover-image-input"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label className="text-slate-300">Tags (comma separated)</Label>
              <Input
                value={blogForm.tags}
                onChange={(e) => handleBlogFormChange('tags', e.target.value)}
                placeholder="export, india, trade"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="blog-tags-input"
              />
            </div>

            {/* Published Toggle */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="blog-published"
                checked={blogForm.published}
                onChange={(e) => handleBlogFormChange('published', e.target.checked)}
                className="rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500"
                data-testid="blog-published-checkbox"
              />
              <Label htmlFor="blog-published" className="text-slate-300 cursor-pointer">
                Publish immediately
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBlogDialog(false)} 
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveBlogPost} 
              disabled={actionLoading}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="blog-save-button"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingPost ? 'Update Post' : 'Create Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
